<template>
  <div class="rounded-2xl border border-gray-200 bg-white p-6 h-full">
    <div
      class="flex flex-col gap-5 mb-6 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <h3 class="text-lg font-semibold text-gray-800">
          Commissions d'Intermédiation Rapidoo
        </h3>
        <p class="mt-1 text-gray-500 text-theme-sm">
          Commissions prélevées sur les commandes restaurants, courses livreurs et livraisons colis
        </p>
      </div>

      <div class="flex items-center gap-3">
        <div
          class="inline-flex items-center gap-0.5 rounded-lg bg-gray-100 p-0.5"
        >
          <button
            v-for="option in options"
            :key="option.value"
            @click="selectPeriod(option)"
            :class="[
              selectedPeriod === option.value
                ? 'shadow-theme-xs text-gray-900 bg-white'
                : 'text-gray-500',
              'px-3 py-2 font-medium rounded-md text-theme-sm hover:text-gray-900 hover:shadow-theme-xs transition-colors',
            ]"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- KPI Cards Summary -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      <div class="p-3.5 rounded-xl border border-indigo-100 bg-indigo-50/40">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-2.5 h-2.5 rounded-full bg-indigo-600"></div>
          <span class="text-xs font-medium text-gray-600">Total Commissions</span>
        </div>
        <p class="text-base sm:text-lg font-bold text-gray-900">
          {{ totals.general.toLocaleString('fr-FR') }}
          <span class="text-xs font-normal text-gray-500">FCFA</span>
        </p>
        <p class="text-[11px] text-gray-400 mt-0.5">Cumul toutes commissions</p>
      </div>

      <div class="p-3.5 rounded-xl border border-orange-100 bg-orange-50/40">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-2.5 h-2.5 rounded-full bg-[#FF7200]"></div>
          <span class="text-xs font-medium text-gray-600">Sur Restaurants</span>
        </div>
        <p class="text-base sm:text-lg font-bold text-gray-900">
          {{ totals.restaurant.toLocaleString('fr-FR') }}
          <span class="text-xs font-normal text-gray-500">FCFA</span>
        </p>
        <p class="text-[11px] text-gray-400 mt-0.5">Commandes repas</p>
      </div>

      <div class="p-3.5 rounded-xl border border-emerald-100 bg-emerald-50/40">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
          <span class="text-xs font-medium text-gray-600">Sur Livreurs</span>
        </div>
        <p class="text-base sm:text-lg font-bold text-gray-900">
          {{ totals.rider.toLocaleString('fr-FR') }}
          <span class="text-xs font-normal text-gray-500">FCFA</span>
        </p>
        <p class="text-[11px] text-gray-400 mt-0.5">Courses livreurs</p>
      </div>

      <div class="p-3.5 rounded-xl border border-sky-100 bg-sky-50/40">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-2.5 h-2.5 rounded-full bg-sky-500"></div>
          <span class="text-xs font-medium text-gray-600">Sur Colis</span>
        </div>
        <p class="text-base sm:text-lg font-bold text-gray-900">
          {{ totals.package.toLocaleString('fr-FR') }}
          <span class="text-xs font-normal text-gray-500">FCFA</span>
        </p>
        <p class="text-[11px] text-gray-400 mt-0.5">Livraisons colis</p>
      </div>
    </div>

    <!-- Chart -->
    <div class="max-w-full overflow-auto custom-scrollbar">
      <div>
        <div class="-ml-4 min-w-[1000px] md:min-w-full pl-2 md:overflow-hidden">
          <VueApexCharts
            type="area"
            height="320"
            :options="chartOptions"
            :series="series"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import VueApexCharts from 'vue3-apexcharts'
import { onMounted, ref, watch } from 'vue'

const props = defineProps({
  statsData: {
    type: Object,
    default: () => ({
      general: null,
      restaurant: null,
      rider: null,
      package: null,
    }),
  },
})

const options = [
  {
    value: 'year',
    label: 'Année',
    periodMarks: [
      'Jan',
      'Fév',
      'Mar',
      'Avr',
      'Mai',
      'Jun',
      'Jul',
      'Aoû',
      'Sep',
      'Oct',
      'Nov',
      'Déc',
    ],
  },
  {
    value: 'month',
    label: 'Mois',
    periodMarks: Array.from({ length: 31 }, (_, i) => (i + 1).toString()),
  },
  {
    value: 'week',
    label: 'Semaine',
    periodMarks: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'],
  },
]

const selectedPeriod = ref('week')

const totals = ref({
  general: 0,
  restaurant: 0,
  rider: 0,
  package: 0,
})

const series = ref([
  {
    name: 'Total Commissions',
    data: [0, 0, 0, 0, 0, 0, 0],
  },
  {
    name: 'Commissions Restaurants',
    data: [0, 0, 0, 0, 0, 0, 0],
  },
  {
    name: 'Commissions Livreurs',
    data: [0, 0, 0, 0, 0, 0, 0],
  },
  {
    name: 'Commissions Colis',
    data: [0, 0, 0, 0, 0, 0, 0],
  },
])

const chartOptions = ref({
  chart: {
    fontFamily: 'Outfit, sans-serif',
    type: 'area',
    height: 320,
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  colors: ['#6366F1', '#FF7200', '#10B981', '#0EA5E9'],
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.35,
      opacityTo: 0.05,
      stops: [0, 90, 100],
    },
  },
  stroke: {
    curve: 'smooth',
    width: [3, 2.5, 2.5, 2.5],
  },
  dataLabels: {
    enabled: false,
  },
  markers: {
    size: 0,
    hover: {
      size: 5,
    },
  },
  legend: {
    show: true,
    position: 'top',
    horizontalAlign: 'left',
    markers: {
      shape: 'circle',
    },
  },
  grid: {
    borderColor: '#F3F4F6',
    strokeDashArray: 4,
    yaxis: {
      lines: {
        show: true,
      },
    },
    xaxis: {
      lines: {
        show: false,
      },
    },
  },
  xaxis: {
    type: 'category',
    categories: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'],
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
    labels: {
      style: {
        colors: '#9CA3AF',
        fontSize: '12px',
      },
    },
  },
  yaxis: {
    labels: {
      style: {
        colors: '#9CA3AF',
        fontSize: '12px',
      },
      formatter: val => {
        if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`
        if (val >= 1000) return `${(val / 1000).toFixed(0)}k`
        return val ? val.toLocaleString('fr-FR') : '0'
      },
    },
  },
  tooltip: {
    theme: 'light',
    y: {
      formatter: val => `${(val || 0).toLocaleString('fr-FR')} FCFA`,
    },
  },
})

function selectPeriod(option) {
  selectedPeriod.value = option.value
  updateChartData()
}

function updateChartData() {
  const periodKey = selectedPeriod.value
  const data = props.statsData || {}

  const generalList = data.general?.[periodKey] || []
  const restaurantList = data.restaurant?.[periodKey] || []
  const riderList = data.rider?.[periodKey] || []
  const packageList = data.package?.[periodKey] || []

  // Extract amounts
  const generalAmounts = generalList.map(item => item.amount || 0)
  const restaurantAmounts = restaurantList.map(item => item.amount || 0)
  const riderAmounts = riderList.map(item => item.amount || 0)
  const packageAmounts = packageList.map(item => item.amount || 0)

  // Update Totals for the selected period
  totals.value = {
    general: generalAmounts.reduce((a, b) => a + b, 0),
    restaurant: restaurantAmounts.reduce((a, b) => a + b, 0),
    rider: riderAmounts.reduce((a, b) => a + b, 0),
    package: packageAmounts.reduce((a, b) => a + b, 0),
  }

  // Update Series
  series.value = [
    { name: 'Total Commissions', data: generalAmounts },
    { name: 'Commissions Restaurants', data: restaurantAmounts },
    { name: 'Commissions Livreurs', data: riderAmounts },
    { name: 'Commissions Colis', data: packageAmounts },
  ]

  // Update Categories from data titles if available
  const activeList = generalList.length
    ? generalList
    : restaurantList.length
      ? restaurantList
      : riderList.length
        ? riderList
        : packageList
  const categories = activeList.map(el => el.title || '')
  if (categories.length > 0) {
    chartOptions.value = {
      ...chartOptions.value,
      xaxis: { ...chartOptions.value.xaxis, categories },
    }
  } else {
    const currentOption = options.find(o => o.value === periodKey)
    if (currentOption) {
      chartOptions.value = {
        ...chartOptions.value,
        xaxis: {
          ...chartOptions.value.xaxis,
          categories: [...currentOption.periodMarks],
        },
      }
    }
  }
}

watch(
  [() => props.statsData, selectedPeriod],
  () => {
    updateChartData()
  },
  { deep: true },
)

onMounted(() => {
  updateChartData()
})
</script>
